import { type Schemas } from "@/types";
import { Box, type MantineStyleProps } from "@mantine/core";
import { type GetInputPropsReturnType } from "node_modules/@mantine/form/lib/types";
import { type QueryParams } from "@/features/entity/api";
import React from "react";
import {
  OptionRender,
  OptionGroupRender,
} from "@/components/Lookup/SingleLookup/Lookup";
import { SingleLookup } from "@/components/Lookup/SingleLookup/SingleLookup";
import { EntityLookup } from "../../Base/Structure/EntityLookup";
import { useLookup } from "@/components/Lookup/Context/useLookup";
import { useTranslation } from "react-i18next";

const ENTITY = "storageTypes";
type ENTITY_TYPE = Schemas["StorageTypeRetrieveDto"];

type StorageTypeLookupProps = GetInputPropsReturnType &
  MantineStyleProps & {
    label?: string;
    required?: boolean;
    disabled?: boolean;
    initial?: ENTITY_TYPE | null;
    initialId?: string | null;
    identifier: string;
  };

export function StorageTypeLookup({
  required = false,
  disabled = false,
  initial,
  initialId,
  identifier,
  ...props
}: StorageTypeLookupProps) {
  const { searchTerm } = useLookup(
    identifier,
    initial?.name,
    initialId,
    initial,
    true,
  );
  const { t } = useTranslation("features");
  const queryParams: QueryParams = {
    pageSize: 50,
  };

  const { infiniteScrollRef, entityList, currentFocus, combobox, isFetching } =
    EntityLookup<ENTITY_TYPE, Schemas["StorageTypeRetrieveDtoPagedList"]>({
      queryParams: queryParams,
      resourcePath: "/api/StorageTypes",
      queryKey: "storageType",
      entity: identifier,
    });

  const header = (
    <React.Fragment>
      <Box>{t("common:lookup.name")}</Box>
    </React.Fragment>
  );

  const options: JSX.Element[] = entityList
    .map((entity, index) => {
      if (!entity) return null;

      const renderedOptions = [
        OptionRender(
          t("storageTypes." + entity.name),
          searchTerm,
          `name_${entity.id}`,
        ),
      ];

      return OptionGroupRender(
        entity.id!,
        index,
        entity.name!,
        entityList.length,
        infiniteScrollRef,
        renderedOptions,
      );
    })
    .filter(Boolean) as JSX.Element[];

  return (
    <SingleLookup<ENTITY_TYPE>
      combobox={combobox}
      required={required}
      disabled={disabled}
      options={options}
      header={header}
      isFetching={isFetching}
      currentFocus={currentFocus}
      identifier={identifier}
      entity={ENTITY}
      entities={entityList}
      {...props}
    />
  );
}
